import {
  CLIENT_REF_KINDS,
  ENDPOINTS,
  ReconcileCorpusRequestSchema,
  type AuthoritativeSowRow,
  type ClientRefKind,
  type ReconcileCorpusRequestInput,
  type ReconcileCorpusResponse,
} from '@clm/shared-types';

import { apiPost } from './client';

/**
 * `reconcile-corpus` (CLM-35, D-B17) — FR-15 over the wire, `api/ingest.ts`'s
 * shape verbatim: path from the registry, request `.parse`d before it leaves the
 * browser (the schema is `.strict()`, so an invented field is a thrown ZodError
 * here and never a payload), response `.parse`d before anything renders.
 *
 * Rule 11 holds: the SPA posts JSON to the gateway and nothing more. The list is
 * parsed **client-side** from a pasted or chosen CSV — `parseListCsv` below is a
 * port of `@clm/reconciliation`'s adapter rules (same header aliases, same RFC
 * 4180 reader), because the SPA may not import that package and a second copy
 * with different rules would make the surface and the CLI disagree about what
 * a usable export is. No file crosses the wire; no SOW number leaves as text
 * anywhere but in the JSON body the contract defines.
 */
const RECONCILE_ENDPOINT = ENDPOINTS['reconcile-corpus'];

export async function reconcileCorpus(
  request: ReconcileCorpusRequestInput,
  signal?: AbortSignal,
): Promise<ReconcileCorpusResponse> {
  const body = ReconcileCorpusRequestSchema.parse(request);
  const raw = await apiPost<unknown>(RECONCILE_ENDPOINT.path, body, signal === undefined ? {} : { signal });
  return RECONCILE_ENDPOINT.response.parse(raw);
}

/* -------------------------------------------------------------------------- */
/* CSV → list rows, client-side                                                */
/* -------------------------------------------------------------------------- */

/** A CSV the surface cannot use. `line` is 1-based and physical, so the message points at the editor line. */
export class ListCsvError extends Error {
  readonly line: number;
  constructor(message: string, line: number) {
    super(message);
    this.name = 'ListCsvError';
    this.line = line;
  }
}

interface CsvRecord {
  readonly fields: readonly string[];
  readonly line: number;
}

/** RFC 4180: quoted fields, doubled quotes, embedded newlines, CRLF/LF, a BOM. */
export function parseCsv(text: string): readonly CsvRecord[] {
  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const records: CsvRecord[] = [];
  let fields: string[] = [];
  let field = '';
  let quoted = false;
  let line = 1;
  let recordLine = 1;
  let sawAny = false;

  const endField = (): void => {
    fields.push(field);
    field = '';
  };
  const endRecord = (): void => {
    endField();
    if (!(fields.length === 1 && fields[0] === '')) records.push({ fields, line: recordLine });
    fields = [];
    sawAny = false;
  };

  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i] as string;
    if (quoted) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        if (ch === '\n') line += 1;
        field += ch;
      }
      continue;
    }
    if (!sawAny) {
      sawAny = true;
      recordLine = line;
    }
    if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      endField();
    } else if (ch === '\r') {
      if (input[i + 1] === '\n') i += 1;
      endRecord();
      line += 1;
    } else if (ch === '\n') {
      endRecord();
      line += 1;
    } else {
      field += ch;
    }
  }
  if (quoted) throw new ListCsvError(`Unterminated quoted field starting on line ${String(recordLine)}.`, recordLine);
  if (sawAny || field !== '' || fields.length > 0) endRecord();
  return records;
}

type CsvColumn = 'sow_number' | ClientRefKind | 'status';

/** The same alias table as `packages/reconciliation/src/adapters/csv-file.ts`. */
const HEADER_ALIASES: Readonly<Record<string, CsvColumn>> = {
  sow_number: 'sow_number',
  sownumber: 'sow_number',
  sow_no: 'sow_number',
  sow: 'sow_number',
  sor_reference_id: 'sor_reference_id',
  client_id: 'sor_reference_id',
  qfly_client_id: 'sor_reference_id',
  sow_initials: 'sow_initials',
  initials: 'sow_initials',
  party_key: 'party_key',
  display_name: 'display_name',
  client_name: 'display_name',
  account_name: 'display_name',
  client: 'display_name',
  status: 'status',
  opportunity_status: 'status',
};

function normaliseHeader(raw: string): string {
  return raw.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

export interface ParseListCsvOptions {
  /** Which client column to read. Required when the header names more than one. */
  readonly clientRefColumn?: ClientRefKind;
}

/**
 * Header-driven: `sow_number`, one client column, optional `status`. A missing
 * column is a {@link ListCsvError} naming the header — the surface renders it as
 * a field error and sends nothing.
 */
export function parseListCsv(text: string, options: ParseListCsvOptions = {}): readonly AuthoritativeSowRow[] {
  const [header, ...data] = parseCsv(text);
  if (header === undefined) throw new ListCsvError('The CSV is empty — it needs a header row.', 1);

  const found = new Map<CsvColumn, number>();
  header.fields.forEach((raw, index) => {
    const column = HEADER_ALIASES[normaliseHeader(raw)];
    if (column !== undefined && !found.has(column)) found.set(column, index);
  });
  const got = header.fields.map((h) => JSON.stringify(h)).join(', ');
  const sowIndex = found.get('sow_number');
  if (sowIndex === undefined) throw new ListCsvError(`The header has no sow_number column (got: ${got}).`, 1);

  let client: { readonly kind: ClientRefKind; readonly index: number };
  if (options.clientRefColumn !== undefined) {
    const index = found.get(options.clientRefColumn);
    if (index === undefined) throw new ListCsvError(`The header has no ${options.clientRefColumn} column (got: ${got}).`, 1);
    client = { kind: options.clientRefColumn, index };
  } else {
    const present = CLIENT_REF_KINDS.filter((kind) => found.has(kind));
    const [only] = present;
    if (only === undefined) {
      throw new ListCsvError(`The header names no client column — expected one of ${CLIENT_REF_KINDS.join(', ')} (got: ${got}).`, 1);
    }
    if (present.length > 1) {
      throw new ListCsvError(`The header names ${String(present.length)} client columns (${present.join(', ')}); choose one above.`, 1);
    }
    client = { kind: only, index: found.get(only) as number };
  }
  const statusIndex = found.get('status') ?? null;

  return data.map((record) => {
    const clientValue = (record.fields[client.index] ?? '').trim();
    if (clientValue === '') throw new ListCsvError(`Line ${String(record.line)} has an empty ${client.kind} cell.`, record.line);
    const status = statusIndex === null ? null : (record.fields[statusIndex] ?? '').trim() || null;
    return {
      sow_number: record.fields[sowIndex] ?? '',
      client_ref: { kind: client.kind, value: clientValue },
      status,
      source_row_ref: `line:${String(record.line)}`,
    };
  });
}
